import React from 'react';
import get from 'lodash/get';
import gtm from 'general/js/gtm';
import { LivePreMessage } from 'components/live-pre-message';
import { LiveCardHighlights } from 'components/live-card-highlights';
import LivePlayerHero from './live-player-hero';
import LivePlayerScorecard from './live-player-scorecard';
import LivePlayerLocation from './live-player-location';
import LivePlayerStats from './live-player-stats';
import LivePlayerRouteToHole from './live-player-route-to-hole';
import {
    playerSitecoreData,
    navTabs,
    playerStatusCodes,
} from './enums';
import {
    getPlayerScoreCardShowStatsSetting,
    getPlayerScoreCardShowR2HClipTagSetting
} from './live-player-helpers';
import { hasValue, isNullOrUndefined, safeParseToInt } from '../../../general/js/general-helpers';
import { formatClipTagParameters, getClipTagParametersFromInitialProps } from '../../shot-tracker/js/shot-tracker-helpers';

const HIGHLIGHTS_TAB = 'highlights';
const SCORE_TAB = 'score';

class LivePlayerLayout extends React.Component {
    constructor(props) {
        super(props);
        this.tabIds = [SCORE_TAB];
        this.state = {
            selectedTabId: SCORE_TAB,
            clipTagParameters: { },
            isClipTagLoaded: false,
            clipTagContainerDiv: null
        };

        this.setClipTagParameters = this.setClipTagParameters.bind(this);
        this.setIsClipTagLoaded = this.setIsClipTagLoaded.bind(this);
        this.setClipTagContainer = this.setClipTagContainer.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { clipTagParameters } = this.state;
        const prevClipTagParameters = getClipTagParametersFromInitialProps(prevProps);

        const { teamNo } = clipTagParameters || {};
        const prevTeamNo = get(prevClipTagParameters, 'teamNo', undefined);

        if (!hasValue(clipTagParameters) || (teamNo !== prevTeamNo)) {
            this.getInitialClipTagParameters();
        }
    }

    getInitialClipTagParameters() {
        const clipTagParameters = getClipTagParametersFromInitialProps(this.props);

        if (!hasValue(clipTagParameters)) {
            return;
        }

        this.setState({ clipTagParameters });
    }

    trackProfile = () => {
        const { firstName, lastName } = this.getSitecoreData();

        gtm.push('Player Card', 'View Player Profile', `${firstName} ${lastName}`);
    };

    setTab = (tabId) => {
        this.setState({ selectedTabId: tabId });
        if (tabId !== SCORE_TAB) {
            this.setIsClipTagLoaded(false);
        }
    }

    getSitecoreData() {
        return get(this.props, 'scoringPlayer.sitecoreData') || playerSitecoreData;
    }

    getTabs() {
        const { highlightGroups } = this.props;

        if (!isNullOrUndefined(highlightGroups)) {
            this.tabIds.push(HIGHLIGHTS_TAB);
        } else {
            if (this.state.selectedTabId === HIGHLIGHTS_TAB) {
                this.setTab(SCORE_TAB);
            }
            this.tabIds = this.tabIds.filter((tab) => tab !== HIGHLIGHTS_TAB);
        }

        return navTabs.filter((tab) => this.tabIds.includes(tab.id));
    }

    getBodyText() {
        const lastname = get(this.props, 'scoringPlayer.sitecoreData.lastName', '');

        if (this.props.liveScanner !== undefined) {
            return `${lastname}${this.props.liveScanner.liveComponents.playerScoreCard.subtitle}`;
        }

        return null;
    }

    setIsClipTagLoaded(value = false) {
        const { isClipTagLoaded } = this.state;

        if (isClipTagLoaded !== value) {
            this.setState({
                isClipTagLoaded: value
            });
        }
    }

    setClipTagContainer(domElement = null) {
        if (isNullOrUndefined(domElement)) {
            return;
        }

        this.setState({
            clipTagContainerDiv: domElement
        });
    }

    setClipTagParameters(newParams) {
        const { clipTagParameters, clipTagContainerDiv } = this.state;

        if (!hasValue(newParams) || !hasValue(clipTagParameters)) {
            return false;
        }

        const { eventCentreParams, playOffData } = newParams;
        const parsedParams = formatClipTagParameters(newParams.teamNo, newParams.round, newParams.hole, eventCentreParams, clipTagContainerDiv, playOffData);
        this.setState({ clipTagParameters: parsedParams });
        return true;
    }

    render() {
        const {
            liveScanner,
            liveShotView,
            sitecorePlayers,
            scoringPlayer,
            scoringStats,
            scoringCourse,
            scoringLeaderboard,
            highlightGroups
        } = this.props;

        const { clipTagParameters, isClipTagLoaded } = this.state;

        if (!scoringPlayer) return null;

        const sitecoreData = this.getSitecoreData();
        const { teamNo } = sitecoreData || {};
        const tabs = this.getTabs();
        const isWithdrawn = scoringPlayer.status &&
            scoringPlayer.status !== playerStatusCodes.ACTIVE;
        const isOnHole = scoringPlayer.hole && scoringPlayer.hole !== 'F';
        const showStatsSection = getPlayerScoreCardShowStatsSetting(this.props);
        const showR2HClipTag = getPlayerScoreCardShowR2HClipTagSetting(this.props) && clipTagParameters.teamNo === safeParseToInt(teamNo);
        const bodyText = this.getBodyText();

        return (
            <article className="live-player">
                <LivePlayerHero
                    scoringLeaderboard={scoringLeaderboard}
                    scoringPlayer={scoringPlayer}
                    tabs={tabs}
                    selectedTabId={this.state.selectedTabId}
                    setTab={this.setTab} />
                {
                    this.state.selectedTabId === SCORE_TAB &&
                    <React.Fragment>
                        <LivePlayerScorecard
                            liveScanner={liveScanner}
                            scoringPlayer={scoringPlayer}
                            scoringCourse={scoringCourse}
                            isWithdrawn={isWithdrawn}
                            isClipTagLoaded={isClipTagLoaded}
                            clipTagParameters={clipTagParameters}
                            setClipTagParameters={this.setClipTagParameters}
                        />
                        {
                            scoringLeaderboard.players &&
                            showR2HClipTag &&
                            <LivePlayerRouteToHole
                                liveShotView={liveShotView}
                                setIsClipTagLoaded={this.setIsClipTagLoaded}
                                clipTagParameters={clipTagParameters}
                                setClipTagParameters={this.setClipTagParameters}
                                setClipTagContainer={this.setClipTagContainer}
                            />
                        }
                        {
                            (!scoringLeaderboard.players ||
                            (scoringLeaderboard.currentRound === 1 &&
                            scoringLeaderboard.currentRoundStatus < 2)) &&
                            !isOnHole &&
                            <LivePreMessage model={{
                                title: liveScanner.liveComponents.playerScoreCard.title,
                                body: bodyText,
                                textColor: liveScanner.liveComponents.playerScoreCard.textColor
                            }} />
                        }
                        {
                            showStatsSection &&
                            !isWithdrawn &&
                            isOnHole &&
                            <LivePlayerLocation
                                holeId={scoringPlayer.hole}
                                scoringPlayer={scoringPlayer}
                                scoringCourse={scoringCourse}
                                scoringStats={scoringStats}
                            />
                        }
                        {
                            showStatsSection &&
                            <LivePlayerStats
                                liveScanner={liveScanner}
                                sitecorePlayers={sitecorePlayers}
                                scoringPlayer={scoringPlayer}
                                scoringStats={scoringStats}
                                isWithdrawn={isWithdrawn}
                            />
                        }
                        {
                            sitecoreData.profileUrl &&
                            <div className="live-player__footer">
                                <a
                                    className="button button--primary"
                                    href={sitecoreData.profileUrl}
                                    onClick={this.trackProfile}
                                >View player profile</a>
                            </div>
                        }
                    </React.Fragment>
                }
                {
                    this.state.selectedTabId === HIGHLIGHTS_TAB &&
                    <LiveCardHighlights videosData={highlightGroups} />
                }
            </article>
        );
    }
}

export default LivePlayerLayout;
